import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";

const WelcomeModal = ({ onNameSubmit }) => {
  const [name, setName] = useState("");
  const [step, setStep] = useState(1);
  const [consentGiven, setConsentGiven] = useState(false);
  const { t, isRTL } = useLanguage();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onNameSubmit(name.trim());
    }
  };

  const handleSkip = () => {
    onNameSubmit("Friend");
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="max-w-md w-full bg-gradient-to-br from-card via-primary/5 to-secondary/5 border-2 border-primary/30 rounded-2xl p-8 shadow-2xl shadow-primary/50"
        >
          {/* Language Selector */}
          <div className="flex justify-end mb-4">
            <LanguageSelector />
          </div>

          {step === 1 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <div className="mb-6 inline-block">
                <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary flex items-center justify-center rounded-2xl shadow-lg shadow-primary/50 mx-auto">
                  <span className="text-5xl">👋</span>
                </div>
              </div>
              
              <h2 className="text-3xl font-mono font-bold mb-3 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                {t('welcomeTitle')} {t('welcomeName')}
              </h2>
              
              <p className="text-white/70 text-base mb-2">
                {t('welcomeSubtitle')}
              </p>
              
              <p className="text-white/60 text-sm mb-6">
                {t('welcomeDescription')}
              </p>
              
              <motion.button
                onClick={() => setStep(2)}
                className="bg-gradient-to-r from-primary to-secondary text-white px-8 py-3 rounded-lg font-mono font-bold uppercase tracking-wider shadow-lg shadow-primary/50 hover:shadow-xl hover:shadow-primary/60 transition-all"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                data-testid="continue-welcome"
              >
                {t('getStarted')}
              </motion.button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-center"
            >
              <div className="mb-6">
                <Sparkles className="w-12 h-12 text-accent mx-auto mb-4" />
              </div>
              
              <h2 className="text-2xl font-mono font-bold mb-3 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                What's Your Name?
              </h2>
              
              <p className="text-white/70 mb-6">
                I'd love to get to know you better!
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('enterName')}
                  className="w-full bg-black/50 border-2 border-primary/30 focus:border-primary rounded-lg px-4 py-3 text-white placeholder:text-white/40 outline-none transition-colors font-mono"
                  autoFocus
                  data-testid="name-input"
                />
                
                {/* Consent Checkbox */}
                <div 
                  className="flex items-start gap-3 p-3 bg-black/30 rounded-lg border border-white/10 cursor-pointer"
                  onClick={() => setConsentGiven(!consentGiven)}
                  data-testid="consent-checkbox-container"
                >
                  <div 
                    className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                      consentGiven 
                        ? 'bg-gradient-to-r from-primary to-secondary' 
                        : 'bg-black/50 border-2 border-white/30'
                    }`}
                  >
                    {consentGiven && <Check size={14} className="text-white" />}
                  </div>
                  <p className="text-white/70 text-xs text-left leading-relaxed">
                    {t('consentText')}{" "}
                    <Link 
                      to="/privacy" 
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                    >
                      {t('privacyPolicy')}
                    </Link>{" "}
                    {t('and')}{" "}
                    <Link 
                      to="/terms" 
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                    >
                      {t('termsOfService')}
                    </Link>
                    . {t('consentDataText')}
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <motion.button
                    type="submit"
                    disabled={!name.trim() || !consentGiven}
                    className="flex-1 bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-lg font-mono font-bold uppercase tracking-wider shadow-lg shadow-primary/50 hover:shadow-xl hover:shadow-primary/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="submit-name"
                  >
                    {t('continue')}
                  </motion.button>
                  
                  <motion.button
                    type="button"
                    onClick={handleSkip}
                    disabled={!consentGiven}
                    className="px-6 py-3 rounded-lg font-mono text-sm text-white/60 hover:text-white/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {t('skip')}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WelcomeModal;
